import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useField } from "formik";
import React from "react";

export interface AutocompleteFieldProps<
  Option,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean = false,
> extends Omit<
    AutocompleteProps<Option, false, DisableClearable, FreeSolo>,
    "value" | "renderInput" | "onBlur"
  > {
  name: string;
  inputLabel: string;
  inputPlaceholder?: string;
  isRequired?: boolean;
  autoFocus?: boolean;
  error?: string;
  loading?: boolean;
  startAdornment?: React.ReactNode;
}

export const AutocompleteField = <
  Option,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean = false,
>({
  name,
  autoFocus,
  disabled,
  isRequired,
  inputLabel,
  inputPlaceholder,
  onChange: onChangeProp,
  loading,
  startAdornment,
  ...props
}: AutocompleteFieldProps<Option, DisableClearable, FreeSolo>) => {
  const [{ value, onBlur }, { touched, error }, { setValue }] =
    useField<any>(name);

  const onChange = React.useCallback(
    async (
      event: React.SyntheticEvent,
      value: Parameters<
        Exclude<
          AutocompleteFieldProps<
            Option,
            DisableClearable,
            FreeSolo
          >["onChange"],
          undefined
        >
      >[1]
    ) => {
      await setValue(value);
    },
    [setValue]
  );

  return (
    <Autocomplete
      value={value}
      // error={touched && Boolean(error)} // having on TextField only is enough
      disabled={disabled}
      loading={loading}
      renderInput={(params: AutocompleteRenderInputParams) => {
        return (
          <TextField
            {...params}
            name={name}
            label={inputLabel}
            placeholder={inputPlaceholder}
            autoFocus={autoFocus}
            required={isRequired}
            error={touched && Boolean(error)}
            helperText={
              touched && typeof error === "string" ? error : undefined
            }
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps?.endAdornment}
                  </>
                ),
                startAdornment:
                  startAdornment || params.InputProps?.startAdornment ? (
                    <>
                      {startAdornment}
                      {params.InputProps?.startAdornment}
                    </>
                  ) : undefined,
              },
            }}
          />
        );
      }}
      onChange={onChangeProp ?? onChange}
      onBlur={onBlur}
      {...props}
    />
  );
};
